import { Box, Drawer, InputAdornment, MenuItem, MuiAutocomplete, OutlinedInput, Popover, Select, TextField } from '@fortune-ui'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import { type HelpTextProps } from '@pets-ui/HelpText/HelpText'
import { HelpText } from '@pets-ui/HelpText/HelpText'
import { CloseSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndexValues } from '@pets-ui/tokens/zIndex.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { getCountryCodeFromPathname } from '@utils'
import { type CountryCode } from 'libphonenumber-js'
import { useEffect, useState, type ChangeEvent, type Dispatch, type KeyboardEvent, type MouseEvent, type ReactNode, type SetStateAction, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { X_PLATFORM_ID_MAP } from '../../../constants/xPlatformIdMap'
import { useRequestCountryList } from '../../../hooks/apis/wp/useRequestCountryList'
import cosignInstance from '../../../i18n/i18nConfig'
import { type Country, type CountryList } from '../../../types/dataModel/platformSdk'
import { useContextCosign } from '../../useContextCosign'
import { CardTitleWithIcon } from './CardTitleWithIcon'
import { ErrorMessage } from './ErrorMessage'

const styles = stylex.create({
  countrySelect: {
    width: '104px',
  },
  phoneInput: {
    fontSize: '14px',
    width: '100%',
  },
  autocompleteCode: {
    color: '#AAAAAA',
    marginLeft: 'auto',
  },
  countryIcon: {
    display: 'inline-block',
    marginRight: '8px',
    width: '16px',
  },
  inputContainer: {
    gap: '12px',
    alignItems: 'flex-start',
    display: 'flex',
  },
  inputContainerGap: (gap: number) => ({
    gap: `${gap}px`,
  }),
  searchIcon: {
    color: '#4B4B4B',
  },
  phoneContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  label: {
    marginBottom: '4px',
  },
})

interface AutocompleteCountryProps {
  onChangeCountryAutocomplete: (event: SyntheticEvent<Element, Event>, option: Country) => void
  countryList: CountryList
}
const AutocompleteCountry = (props: AutocompleteCountryProps) => {
  const { t } = useTranslation('translation', { i18n: cosignInstance })
  const { onChangeCountryAutocomplete, countryList } = props
  return (
    <MuiAutocomplete
      open={true}
      options={countryList}
      disablePortal={true}
      sx={{
        background: '#fff',
        zIndex: 1400,
        top: 0,
        position: 'sticky',
        left: 0,
        paddingTop: '8px',
        marginBottom: '8px',
      }}
      clearOnBlur={false}
      getOptionLabel={(option: Country) => `${option.countryIsoCode} （${t(`country.${option.countryIsoCode}`)}） ${option.callingCodes}`}
      onChange={(e, value) => {
        if (!value) return
        onChangeCountryAutocomplete(e, value)
      }}
      noOptionsText={<Text variant="body" align="center">{t('login.noMatching')}</Text>}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            fontSize: '14px',
            borderRadius: '4px',
            marginBottom: '8px',
            marginTop: { xs: '8px', md: 0 },
            height: '40px',
          }}
        >
          <img
            loading="lazy"
            width="20"
            {...stylex.props(styles.countryIcon)}
            srcSet={`https://flagcdn.com/w40/${option.countryIsoCode.toLowerCase()}.png 2x`}
            src={`https://flagcdn.com/w20/${option.countryIsoCode.toLowerCase()}.png`}
            alt={option.countryIsoCode}
          />
          <Text variant="body">
            {t(`country.${option.countryIsoCode}`)}
            （
            {option.countryIsoCode}
            ）
          </Text>
          <Text variant="body" rootStyle={styles.autocompleteCode}>
            {option.callingCodes}
          </Text>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          sx={{
            '& .MuiInputBase-root': {
              py: '0 !important',
            },
            '& legend': { display: 'none' },
            '& fieldset': { top: 0, border: 'none' },
            'background': '#F7F7F7',
            'borderRadius': '4px',
            'input': {
              border: 'none',
              background: 'none',
              boxShadow: 'none',
            },
            'input:focus': {
              background: 'none',
              boxShadow: 'none',
            },
          }}
          placeholder={String(t('login.search'))}
          size="small"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: null,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon {...stylex.props(styles.searchIcon)} />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
      )}
    />
  )
}

const popoverSx = {
  '& .MuiPopover-paper': {
    'minWidth': '300px',
    'maxHeight': '300px',
    'overflow': 'auto',
    'marginTop': '12px',
    'padding': '0px 8px 8px 8px',
    '& .MuiAutocomplete-popper': {
      'position': 'relative !important',
      'inset': '0 !important',
      'transform': 'translate(0) !important',
      'maxHeight': 'auto',
      '& .MuiAutocomplete-noOptions': {
        paddingTop: '24px',
      },
    },
    '& .MuiAutocomplete-paper': {
      'boxShadow': '0 0 0 !important',
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        maxHeight: 'none',
      },
    },
  },
}

interface InputInternationalPhoneProps {
  phone: string
  setPhone: (phone: string) => void
  phoneError?: string
  validatePhone?: () => void
  helpText?: ReactNode
  HelpTextProps?: HelpTextProps
  countryCode?: Country['countryIsoCode']
  setCountryCode: Dispatch<SetStateAction<Country['countryIsoCode'] | undefined>>
  label?: ReactNode
  inputGap?: number
}

const appEnv = import.meta.env.VITE_APP_ENV === 'development' ? 'dev' : import.meta.env.VITE_APP_ENV

export function InputInternationalPhoneNumber(props: InputInternationalPhoneProps) {
  const { t } = useTranslation('translation', { i18n: cosignInstance })
  const { projectCode } = useContextCosign()
  const { phone, setPhone, phoneError, validatePhone, helpText, HelpTextProps, countryCode, setCountryCode, label, inputGap = 12 } = props
  const country = getCountryCodeFromPathname(window.location.pathname, 'tw')
  const isMobile = useResponsive('mobile')
  const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(null)
  const [openDrawer, setOpenDrawer] = useState(false)
  const { destructuredData: countryList } = useRequestCountryList({ 'x-platform-id': String(X_PLATFORM_ID_MAP[(appEnv ?? 'dev') as keyof typeof X_PLATFORM_ID_MAP][projectCode]) })
  useEffect(() => {
    if (!!countryList?.length && !countryCode) {
      // @ts-expect-error - TODO: fix this
      setCountryCode(country ?? countryList[0].countryIsoCode as CountryCode)
    }
  }, [country, countryCode, countryList, setCountryCode])
  const selectCountries = countryList?.map(({ callingCodes, countryIsoCode }) => {
    return { key: countryIsoCode, value: callingCodes }
  }) ?? []

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorElement(event.currentTarget)
    setOpenDrawer(true)
  }

  const handleClose = () => {
    setAnchorElement(null)
  }

  const toggleDrawer = (event: KeyboardEvent | MouseEvent) => {
    if (
      event.type === 'keydown'
      && ((event as KeyboardEvent).key === 'Tab'
        || (event as KeyboardEvent).key === 'Shift')
    ) {
      return
    }

    setOpenDrawer(!openDrawer)
  }

  const handleChangeCountryAutocomplete = (_event: SyntheticEvent<Element, Event>, value: Country) => {
    setCountryCode(value.countryIsoCode as CountryCode)
    setOpenDrawer(false)
    setAnchorElement(null)
  }

  return (
    <Box width="100%">
      {!isMobile
        ? (
          <Popover
            open={!!anchorElement}
            anchorEl={anchorElement}
            onClose={handleClose}
            elevation={4}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            disablePortal
            sx={popoverSx}
          >
            <AutocompleteCountry
              countryList={countryList ?? []}
              onChangeCountryAutocomplete={handleChangeCountryAutocomplete}
            />
          </Popover>
        )
        : (
          <Drawer
            anchor="bottom"
            open={openDrawer}
            onClose={toggleDrawer}
            sx={{
              zIndex: zIndexValues.modal,
              '& .MuiDrawer-paper': {
                'height': '400px',
                'borderTopLeftRadius': '16px',
                'borderTopRightRadius': '16px',
                'padding': '16px 16px 0 16px',
                'overflow': 'hidden',
                '& .MuiAutocomplete-paper': {
                  'boxShadow': '0 0 0 !important',
                  '& .MuiAutocomplete-listbox': {
                    paddingTop: '0',
                    maxHeight: 'none',
                  },
                },
                '& .MuiAutocomplete-popperDisablePortal': {
                  maxHeight: 'calc(100% - 92px)',
                  overflow: 'auto',
                },
              },
            }}
          >
            <CardTitleWithIcon
              title={t('login.selectCountry')}
              rightIcon={<CloseSmall />}
              onClickRight={toggleDrawer}
            />
            <AutocompleteCountry
              countryList={countryList ?? []}
              onChangeCountryAutocomplete={handleChangeCountryAutocomplete}
            />
          </Drawer>
        )}
      <>
        {label && <Text rootStyle={styles.label} variant="body">{label}</Text>}
        <div {...stylex.props(styles.inputContainer, styles.inputContainerGap(inputGap))}>
          <Select
            key={countryCode}
            sx={{
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiSelect-select': {
                paddingRight: '24px !important',
              },
              '& .MuiOutlinedInput-input': {
                'color': `${colors.grey800} !important`,
                '-webkit-text-fill-color': `${colors.grey800} !important`,
              },
              '& .MuiSvgIcon-root': {
                color: `${colors.grey800} !important`,
              },
              'height': '40px',
              'fontSize': '14px',
            }}
            {...stylex.props(styles.countrySelect)}
            variant="outlined"
            IconComponent={KeyboardArrowDownIcon}
            options={selectCountries}
            value={countryCode ?? ''}
            disabled
            onClick={handleClick}
            renderOption={selectCountries.map(option => (
              <MenuItem key={option.key} value={option.key}>
                <img
                  loading="lazy"
                  {...stylex.props(styles.countryIcon)}
                  srcSet={`https://flagcdn.com/w40/${option.key.toLowerCase()}.png 2x`}
                  src={`https://flagcdn.com/w20/${option.key.toLowerCase()}.png`}
                  alt={option.key}
                />
                {option.value}
              </MenuItem>
            ))}
          />
          <div {...stylex.props(styles.phoneContainer)}>
            <OutlinedInput
              sx={{
                'height': '40px',
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${colors.grey800}`,
                  },
                },
              }}
              name="username"
              size="small"
              placeholder={String(t('login.pleaseEnter'))}
              value={phone}
              type="tel"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target
                const regex = /^\d+$/
                if (!regex.test(event.target.value) && value !== '') {
                  event.preventDefault()
                  return
                }
                setPhone(event.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && phone.trim() !== '') {
                  validatePhone?.()
                }
              }}
              componentsProps={{
                input: {
                  style: {
                    boxShadow: 'none',
                    border: 'none',
                  },
                },
              }}
              error={!!phoneError}
              {...stylex.props(styles.phoneInput)}
            />
            {helpText && (
              <HelpText
                {...HelpTextProps}
                rootStyle={HelpTextProps?.rootStyle}
              >
                {helpText}
              </HelpText>
            )}
            {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
          </div>
        </div>
      </>
    </Box>

  )
}
